import { TagIcon, cn } from '@playerbase/ui';

import { BuildMarkerLabel } from '../games/build-marker-label.js';
import type { BuildMarkerSummary } from '../games/build-timeline.js';

// ---------------------------------------------------------------------------
// Compact activity-panel entry for a build going live.
//
// Mirrors the look of `form-submission-activity-item.tsx` (a source-label
// band over a short body) but carries a build marker rather than a feed
// row. Clicking it routes to the Messages feed and scrolls to the matching
// "build changed" divider (the divider rows land in PR3; until then the
// click just selects the Messages tab).
// ---------------------------------------------------------------------------

interface BuildMarkerActivityItemProps {
  marker: BuildMarkerSummary;
  changedAt: string;
  onSelect: () => void;
  unread: boolean;
}

const ONE_MINUTE_MS = 60_000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

function formatRelativePast(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  if (diff < ONE_MINUTE_MS) return 'just now';
  if (diff < ONE_HOUR_MS) return `${Math.floor(diff / ONE_MINUTE_MS)}m ago`;
  if (diff < ONE_DAY_MS) return `${Math.floor(diff / ONE_HOUR_MS)}h ago`;
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function BuildMarkerActivityItem({
  marker,
  changedAt,
  onSelect,
  unread,
}: BuildMarkerActivityItemProps): JSX.Element {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'block cursor-pointer transition-colors',
        'hover:bg-surface-3',
        'focus:outline-none focus-visible:bg-surface-3',
      )}
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-2 text-xs text-text-muted">
        <span className="truncate">build changed</span>
        {unread && (
          <span
            aria-hidden
            className="block h-2 w-2 shrink-0 rounded-full bg-accent motion-safe:animate-glow"
          />
        )}
      </div>
      <div className="flex items-start gap-2 px-4 pb-3 pt-1">
        <TagIcon className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text">
            <BuildMarkerLabel marker={marker} />
          </div>
          <div className="mt-0.5 text-xs text-text-muted">
            {formatRelativePast(changedAt)}
          </div>
        </div>
      </div>
    </div>
  );
}
